import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import {
  BessForecastResponse,
  BessFrequencyEvent,
  BessSchedule,
  BessSettlement,
  BessSocLog,
  ForecastPriceBulkFetchResult,
  ForecastPriceSnapshot,
  ForecastPriceSnapshotDetail,
} from '../../../core/models/domain.models';

@Injectable({ providedIn: 'root' })
export class BessOperationsService {
  private readonly api = inject(ApiService);

  listSocLogs() {
    return this.api.get<BessSocLog[]>('/api/bess/soc-logs');
  }

  createSocLog(payload: Partial<BessSocLog>) {
    return this.api.post<BessSocLog>('/api/bess/soc-logs', payload);
  }

  updateSocLog(id: string, payload: Partial<BessSocLog>) {
    return this.api.patch<BessSocLog>(`/api/bess/soc-logs/${id}`, payload);
  }

  deleteSocLog(id: string) {
    return this.api.delete<{ message: string }>(`/api/bess/soc-logs/${id}`);
  }

  listSchedules() {
    return this.api.get<BessSchedule[]>('/api/bess/schedules');
  }

  createSchedule(payload: Partial<BessSchedule>) {
    return this.api.post<BessSchedule>('/api/bess/schedules', payload);
  }

  updateSchedule(id: string, payload: Partial<BessSchedule>) {
    return this.api.patch<BessSchedule>(`/api/bess/schedules/${id}`, payload);
  }

  deleteSchedule(id: string) {
    return this.api.delete<{ message: string }>(`/api/bess/schedules/${id}`);
  }

  listSettlements() {
    return this.api.get<BessSettlement[]>('/api/bess/settlements');
  }

  createSettlement(payload: Partial<BessSettlement>) {
    return this.api.post<BessSettlement>('/api/bess/settlements', payload);
  }

  updateSettlement(id: string, payload: Partial<BessSettlement>) {
    return this.api.patch<BessSettlement>(`/api/bess/settlements/${id}`, payload);
  }

  deleteSettlement(id: string) {
    return this.api.delete<{ message: string }>(`/api/bess/settlements/${id}`);
  }

  listFrequencyEvents() {
    return this.api.get<BessFrequencyEvent[]>('/api/bess/frequency-events');
  }

  createFrequencyEvent(payload: Partial<BessFrequencyEvent>) {
    return this.api.post<BessFrequencyEvent>('/api/bess/frequency-events', payload);
  }

  updateFrequencyEvent(id: string, payload: Partial<BessFrequencyEvent>) {
    return this.api.patch<BessFrequencyEvent>(`/api/bess/frequency-events/${id}`, payload);
  }

  deleteFrequencyEvent(id: string) {
    return this.api.delete<{ message: string }>(`/api/bess/frequency-events/${id}`);
  }

  listForecasts(product: 'DAM' | 'RTM', forecastDate?: string) {
    const params: Record<string, string> = { product };
    if (forecastDate) params['forecast_date'] = forecastDate;
    return this.api.get<BessForecastResponse>('/api/bess/forecasts', params);
  }

  listForecastPrices(product?: 'DAM' | 'GDAM' | 'RTM', forecastDate?: string) {
    const params: Record<string, string> = {};
    if (product) params['product'] = product;
    if (forecastDate) params['forecast_date'] = forecastDate;
    return this.api.get<ForecastPriceSnapshot[]>('/api/forecast-prices', params);
  }

  getForecastPrice(snapshotId: string) {
    return this.api.get<ForecastPriceSnapshotDetail>(`/api/forecast-prices/${snapshotId}`);
  }

  autoFetchForecastPrice(product: 'DAM' | 'GDAM' | 'RTM', forecastDate?: string, forceRefetch = false) {
    const params: Record<string, string> = { product, force_refetch: String(forceRefetch) };
    if (forecastDate) params['forecast_date'] = forecastDate;
    return this.api.post<ForecastPriceBulkFetchResult>(`/api/forecast-prices/auto-fetch?${new URLSearchParams(params).toString()}`, {});
  }

  refetchForecastPrice(snapshotId: string) {
    return this.api.post<ForecastPriceSnapshotDetail>(`/api/forecast-prices/${snapshotId}/refetch`, {});
  }
}
